import { ApiError, errorMessage, parseErrorResponse } from "./errors";
import type { LoginResponse, RefreshRequest } from "./types";

/**
 * API taban adresi. Backend bağlandığında `VITE_API_BASE` ile değiştirilir
 * (ör. `https://panel.example.com/api/v1`). Varsayılan Vite proxy uyumlu göreli yoldur.
 */
export const API_BASE: string =
  import.meta.env.VITE_API_BASE ?? "/api/v1";

/** Oturum jetonlarının tarayıcıdaki saklama anahtarları. */
const ACCESS_TOKEN_KEY = "nagios.accessToken";
const REFRESH_TOKEN_KEY = "nagios.refreshToken";

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}

/** localStorage'daki jetonları okur; storage kapalıysa `null`. */
function readStoredTokens(): SessionTokens | null {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (accessToken && refreshToken) return { accessToken, refreshToken };
  } catch {
    /* storage kapalı */
  }
  return null;
}

/**
 * Jeton deposu. localStorage kapalıysa bellekte tutar; kalıcılık kaybı olur
 * ama oturum yine de çalışır. `set`/`clear` değişikliklerini diğer sekmelere
 * duyurur (aşağıdaki sekmeler arası eşitleme).
 */
export const tokenStore = {
  get(): SessionTokens | null {
    return readStoredTokens() ?? memoryTokens;
  },
  set(tokens: SessionTokens): void {
    memoryTokens = tokens;
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    } catch {
      /* storage kapalı */
    }
    publishTokens(tokens);
  },
  clear(): void {
    memoryTokens = null;
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      /* storage kapalı */
    }
    publishTokens(null);
  },
};

let memoryTokens: SessionTokens | null = null;

/* ---------------------------------------------------------------------------
 * Sekmeler arası oturum eşitleme (Hata B — çoklu sekmede çift rotasyon).
 *
 * Sunucuda refresh token rotasyonu var: kullanılmış bir refresh token'ın
 * tekrar gönderilmesi tüm token ailesini iptal eder. Sekmeler aynı localStorage
 * kaydını paylaştığı için bir sekmenin rotasyonu diğerini bayat bırakır.
 * Rotasyon sonrası yeni jeton setini `BroadcastChannel("nagios-auth")` ile
 * duyururuz; kanal yoksa tarayıcının localStorage yazmaları için diğer
 * sekmelere zaten gönderdiği `storage` olayına sessizce düşülür. Alan sekme
 * yalnızca bellek kopyasını günceller — localStorage zaten ortaktır.
 * ------------------------------------------------------------------------- */
const AUTH_SYNC_CHANNEL = "nagios-auth";

type AuthSyncMessage =
  | { type: "tokens"; accessToken: string; refreshToken: string }
  | { type: "clear" };

let authSync: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    authSync = new BroadcastChannel(AUTH_SYNC_CHANNEL);
  }
} catch {
  authSync = null; // Kanal açılamıyorsa storage olayı devralır.
}

function publishTokens(tokens: SessionTokens | null): void {
  if (!authSync) return;
  const message: AuthSyncMessage = tokens
    ? { type: "tokens", accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }
    : { type: "clear" };
  try {
    authSync.postMessage(message);
  } catch {
    /* sayfa kapanıyor olabilir — storage olayı zaten yedek kanal */
  }
}

if (authSync) {
  authSync.addEventListener("message", (event: MessageEvent) => {
    const message = event.data as AuthSyncMessage | undefined;
    if (!message || typeof message !== "object") return;
    if (
      message.type === "tokens" &&
      typeof message.accessToken === "string" &&
      typeof message.refreshToken === "string"
    ) {
      memoryTokens = { accessToken: message.accessToken, refreshToken: message.refreshToken };
    } else if (message.type === "clear") {
      memoryTokens = null;
    }
  });
}

// Yedek kanal: diğer sekmelerin localStorage yazmaları storage olayı tetikler;
// bellek kopyasını taze tut (storage kapalıyken get() bunu okur).
window.addEventListener("storage", (event) => {
  if (event.key === null) {
    memoryTokens = null; // localStorage.clear()
    return;
  }
  if (event.key !== ACCESS_TOKEN_KEY && event.key !== REFRESH_TOKEN_KEY) return;
  memoryTokens = readStoredTokens();
});

/** Oturumun yenilenemeyip sonlandırıldığını bildiren olay adı. */
export const SESSION_EXPIRED_EVENT = "nagios:session-expired";

/**
 * Oturumun GEÇİCİ bir hataya (429/5xx/ağ) takılıp yenilenemediğini, ama
 * açık kaldığını bildiren olay adı. Abone: AuthContext (toast gösterir).
 */
export const SESSION_RETRY_EVENT = "nagios:session-retry";

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  /** Sorgu parametreleri; `undefined` ve boş string olanlar atlanır. */
  query?: Record<string, string | number | boolean | undefined>;
  /** JSON gövde. */
  body?: unknown;
  /** false ise Authorization başlığı eklenmez (login/refresh). */
  auth?: boolean;
  /** 401'de refresh denenmesin (refresh isteğinin kendisi). */
  retryOnUnauthorized?: boolean;
}

export type QueryValue = string | number | boolean | undefined;

export function buildUrl(
  path: string,
  query?: Record<string, QueryValue>,
): string {
  const url = new URL(path.startsWith("http") ? path : `${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/** Refresh denemesinin sonucu. */
export type RefreshOutcome = "refreshed" | "fatal" | "transient";

/**
 * Sözleşmedeki kesin reddetme kodları (4xx gövdesi). Bunlardan biri gelirse
 * refresh token ölü demektir; denemeyi sürdürmek aileyi yakmaz ama oturum
 * zaten bitmiştir.
 */
const FATAL_REFRESH_CODES = new Set([
  "invalid_grant",
  "invalid_refresh_token",
  "refresh_token_reused",
  "refresh_token_revoked",
  "refresh_token_expired",
  "invalid_token",
  "session_expired",
  "unauthorized",
]);

/**
 * Hata A: refresh başarısızlığını sınıflandırır.
 *
 * Yalnız KESİN reddetme ölümcüldür: 401/403 veya `invalid_grant` gibi
 * sözleşme reddetme kodu taşıyan 4xx → token'lar silinir, oturum biter.
 * `429` (rate_limited), 5xx ve ağ hataları (fetch'in kendisinin patlaması)
 * GEÇİCİDİR: token'lar korunur, sonraki istek refresh'i yeniden dener.
 * Aksi halde tek bir rate limit bile 15 dakikalık oturumu çöpe atardı.
 */
export function classifyRefreshFailure(
  error: unknown,
): Exclude<RefreshOutcome, "refreshed"> {
  if (error instanceof ApiError) {
    const { status, code } = error;
    if (status === 429 || status >= 500) return "transient";
    if (status === 401 || status === 403) return "fatal";
    if (status >= 400 && status < 500 && FATAL_REFRESH_CODES.has(code)) return "fatal";
    return "transient";
  }
  // Ağ hatası (TypeError) / timeout / CORS — oturum ölmüş sayılmaz.
  return "transient";
}

/**
 * Hata oturumun KESİN bittiğini mi gösteriyor? (401/403 veya sözleşmedeki
 * kalıcı reddetme kodu.) 429/5xx/ağ hatalarında `false` döner: oturum korunur.
 */
export function isFatalAuthError(error: unknown): boolean {
  return classifyRefreshFailure(error) === "fatal";
}

/** Refresh isteklerini eşzamanlı tetiklenirse tek seferde toplar (sekme içi). */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Tek-uyçu refresh: aynı anda bekleyen istekler aynı sonucu paylaşır, böylece
 * karar (ölümcül/geçici) bir kez verilir ve geçici hatada token'lar yarışla
 * silinmez.
 */
async function refreshSession(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const tokens = tokenStore.get();
    if (!tokens) return "fatal" as const;
    try {
      // En taze refresh token depodan okunur; rotasyona uğramış token asla
      // ikinci kez gönderilmez (aile iptali sunucu tasarımı).
      const response = await request<LoginResponse>("/auth/refresh", {
        method: "POST",
        body: { refreshToken: tokens.refreshToken } satisfies RefreshRequest,
        auth: false,
        retryOnUnauthorized: false,
      });
      tokenStore.set({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });
      return "refreshed" as const;
    } catch (error) {
      if (classifyRefreshFailure(error) === "fatal") {
        tokenStore.clear();
        return "fatal" as const;
      }
      // Geçici hata: oturumu KORU. Konsol uyarısı + olay (AuthContext toast
      // gösterir); çağıran istek kendi ApiError'ı ile zaten hata durumunu gösterir.
      console.warn(
        "[auth] Oturum yenilenemedi (geçici hata); oturum korunuyor, sonraki istekte yeniden denenecek —",
        errorMessage(error),
      );
      window.dispatchEvent(new CustomEvent(SESSION_RETRY_EVENT));
      return "transient" as const;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Fetch tabanlı istemci gövdesi.
 *
 * - `Authorization: Bearer <accessToken>` ekler,
 * - 401 alırsa önce depoda başka sekmenin tazelediği token olup olmadığına
 *   bakar, yoksa refresh dener ve isteği tekrarlar,
 * - hata gövdesini `ApiError` (tipi `ErrorResponse`) olarak fırlatır.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", query, body, auth = true, retryOnUnauthorized = true } = options;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let usedAccessToken: string | null = null;
  if (auth) {
    const tokens = tokenStore.get();
    if (tokens) {
      headers["Authorization"] = `Bearer ${tokens.accessToken}`;
      usedAccessToken = tokens.accessToken;
    }
  }

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    if (response.status === 401 && auth && retryOnUnauthorized) {
      // Hata B.1: ölümcül yola girmeden önce depoyu yokla — diğer sekme
      // arada yenilediyse access token değişmiştir; refresh'e hiç gerek
      // kalmadan taze token ile bir kez yinele (refresh = rotasyon riski).
      const current = tokenStore.get();
      if (current && current.accessToken !== usedAccessToken) {
        return request<T>(path, { ...options, retryOnUnauthorized: false });
      }
      const outcome = await refreshSession();
      if (outcome === "refreshed") {
        return request<T>(path, { ...options, retryOnUnauthorized: false });
      }
      if (outcome === "fatal") {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      // "transient": oturum açık kalır; aşağıdaki orijinal ApiError sayfada
      // hata durumu olarak görünür, bir sonraki istek refresh'i yeniden dener.
    }
    throw new ApiError(response.status, parseErrorResponse(payload));
  }

  return payload as T;
}
